import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { MIN, leer, guardar, arrastrar } from './useAnchos'

// Column widths you set by dragging a header's right edge, as on monday.
// Two pieces:
//   * useAnchos + AsaAncho: for a table that draws its own widths (a
//     <colgroup>), e.g. the work orders list, whose groups share them.
//   * RedimensionarTablas: mounted once (Layout), gives every other <table>
//     of the app the same grip, without touching each one. A table opts out
//     with data-anchos-propios.
// Widths are remembered in this browser (localStorage); a double click on
// the grip gives the column back its automatic width.

// The grip on a header's right edge (the header needs position: relative or sticky).
export function AsaAncho({ onArrastre, onReiniciar }) {
  return (
    <span role="separator" aria-orientation="vertical" onMouseDown={onArrastre} onDoubleClick={onReiniciar}
      onClick={e => e.stopPropagation()}
      className="absolute top-0 right-0 z-20 h-full w-2 translate-x-1/2 cursor-col-resize group/asa">
      <span className="absolute inset-y-1 left-1/2 w-0.5 -translate-x-1/2 rounded bg-transparent group-hover/asa:bg-indigo-400" />
    </span>
  )
}

// --- Every other table of the app ---

const MARCA = 'data-redim'

// The header row whose cells are the columns: the last one of the thead
// (multi-row headers put the leaf columns there), cells without colSpan.
function filaColumnas(tabla) {
  const filas = tabla.tHead?.rows
  return filas?.length ? filas[filas.length - 1] : null
}

function claveDe(tabla, ruta) {
  const fila = filaColumnas(tabla)
  const firma = Array.from(fila?.cells || []).map(c => (c.textContent || '').trim().slice(0, 24)).join('|')
  return `anchos:${ruta}:${firma}`
}

// Freeze the table at its current widths (fixed layout), so a column can
// also be made narrower than its content.
function fijar(tabla, fila, guardados = {}) {
  const celdas = Array.from(fila.cells)
  const anchos = celdas.map((c, i) => guardados[i] ?? c.getBoundingClientRect().width)
  celdas.forEach((c, i) => {
    if (c.colSpan > 1) return
    c.style.width = `${anchos[i]}px`
    c.style.minWidth = `${anchos[i]}px`
    c.style.maxWidth = `${anchos[i]}px`
    c.style.overflow = 'hidden'
    c.style.textOverflow = 'ellipsis'
  })
  tabla.style.tableLayout = 'fixed'
  tabla.style.width = `${anchos.reduce((s, w) => s + w, 0)}px`
  tabla.style.minWidth = '0'
  tabla.dataset.redimFija = '1'
}

function preparar(tabla, ruta) {
  const fila = filaColumnas(tabla)
  if (!fila || !fila.cells.length) return
  tabla.setAttribute(MARCA, '1')
  const clave = claveDe(tabla, ruta)
  Array.from(fila.cells).forEach((th, i) => {
    if (th.colSpan > 1 || th.querySelector(':scope > [data-asa-ancho]')) return
    if (getComputedStyle(th).position === 'static') th.style.position = 'relative'
    const asa = document.createElement('span')
    asa.setAttribute('data-asa-ancho', '')
    asa.setAttribute('role', 'separator')
    asa.className = 'absolute top-0 right-0 z-20 h-full w-2 translate-x-1/2 cursor-col-resize hover:bg-indigo-300/60'
    asa.addEventListener('click', e => e.stopPropagation())
    asa.addEventListener('mousedown', e => {
      if (!tabla.dataset.redimFija) fijar(tabla, fila)
      const inicio = th.getBoundingClientRect().width
      const total = tabla.getBoundingClientRect().width
      arrastrar(e, dx => {
        const w = Math.max(MIN, Math.round(inicio + dx))
        th.style.width = th.style.minWidth = th.style.maxWidth = `${w}px`
        tabla.style.width = `${Math.round(total - inicio + w)}px`
      }, () => guardar(clave, { ...leer(clave), [i]: Math.round(th.getBoundingClientRect().width) }))
    })
    asa.addEventListener('dblclick', e => {
      e.stopPropagation()
      const g = leer(clave)
      delete g[i]
      guardar(clave, g)
      // Back to automatic layout, then re-apply what is still saved.
      Array.from(fila.cells).forEach(c => { c.style.width = c.style.minWidth = c.style.maxWidth = '' })
      tabla.style.tableLayout = tabla.style.width = tabla.style.minWidth = ''
      delete tabla.dataset.redimFija
      if (Object.keys(g).length) fijar(tabla, fila, g)
    })
    th.appendChild(asa)
  })
  // Saved widths: applied once the table has rows (measured with its content).
  const guardados = leer(clave)
  if (Object.keys(guardados).length && !tabla.dataset.redimFija && tabla.tBodies[0]?.rows.length) {
    fijar(tabla, fila, guardados)
  }
}

export function RedimensionarTablas() {
  const { pathname } = useLocation()
  useEffect(() => {
    let pendiente = null
    const recorrer = () => {
      pendiente = null
      document.querySelectorAll('table').forEach(tabla => {
        if (tabla.closest('[data-anchos-propios]')) return
        const marcada = tabla.getAttribute(MARCA)
        const fila = filaColumnas(tabla)
        // New table, or React replaced its header (the grips are gone).
        if (!marcada || (fila && !fila.querySelector('[data-asa-ancho]')) || !tabla.dataset.redimFija) preparar(tabla, pathname)
      })
    }
    const obs = new MutationObserver(() => { if (!pendiente) pendiente = requestAnimationFrame(recorrer) })
    obs.observe(document.body, { childList: true, subtree: true })
    recorrer()
    return () => { obs.disconnect(); if (pendiente) cancelAnimationFrame(pendiente) }
  }, [pathname])
  return null
}
